import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { API } from '../App';
import { Loader2, Save, Plus, Trash2, ChevronLeft, ChevronRight, Download, FileText, AlertTriangle, CheckCircle2 } from 'lucide-react';

const STEPS = [
  { id: 1, label: 'Personal' },
  { id: 2, label: 'Target Career' },
  { id: 3, label: 'Experience' },
  { id: 4, label: 'Education' },
  { id: 5, label: 'Skills' },
  { id: 6, label: 'Additional' },
  { id: 7, label: 'Review & Generate' },
];

const EMPTY_EXP = { job_title: '', employer: '', location: '', start_date: '', end_date: '', is_current: false, responsibilities: [''], achievements: [''], tools: [''] };
const EMPTY_EDU = { degree: '', field_of_study: '', institution: '', location: '', year: '', grade: '' };

const emptyDraft = () => ({
  current_step: 1,
  personal: {},
  target: { country: 'UAE', experience_level: 'Experienced' },
  experiences: [{ ...EMPTY_EXP }],
  education: [{ ...EMPTY_EDU }],
  skills: { core_skills: [], tools_software: [], languages: [], certifications: [], trainings: [] },
  additional: {},
  target_jd: '',
});

export default function CVBuilderPage() {
  const nav = useNavigate();
  const [draft, setDraft] = useState(emptyDraft);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(null);
  const [template, setTemplate] = useState('ats_professional');
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [downloadingDocx, setDownloadingDocx] = useState(false);
  const [versions, setVersions] = useState([]);
  const [showVersions, setShowVersions] = useState(false);
  const saveTimer = useRef(null);

  // ---- restore draft on load ----
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/cv-builder/draft`);
        if (r.data.draft) {
          setDraft({ ...emptyDraft(), ...r.data.draft });
          setStep(r.data.draft.current_step || 1);
          toast.success('Your saved draft has been restored');
        }
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  const fetchVersions = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/cv-builder/versions`);
      setVersions(r.data.versions || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchVersions(); }, [fetchVersions]);

  // ---- debounced auto-save ----
  const scheduleSave = useCallback((nextDraft, nextStep) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        await axios.post(`${API}/cv-builder/draft`, { ...nextDraft, current_step: nextStep });
        setSavedAt(new Date());
      } catch { /* silent */ }
      finally { setSaving(false); }
    }, 800);
  }, []);

  const saveAndExit = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/cv-builder/draft`, { ...draft, current_step: step });
      toast.success('Draft saved. You can resume anytime.');
      nav('/dashboard');
    } catch {
      toast.error('Could not save draft — please check your connection');
    } finally { setSaving(false); }
  };

  const update = (path, value) => {
    setDraft(prev => {
      const next = structuredClone(prev);
      const keys = path.split('.');
      let obj = next;
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
      obj[keys[keys.length - 1]] = value;
      scheduleSave(next, step);
      return next;
    });
  };

  const goStep = async (target) => {
    setStep(target);
    await axios.post(`${API}/cv-builder/draft`, { ...draft, current_step: target }).catch(() => {});
  };

  // ---- validators ----
  const stepValid = (s) => {
    if (s === 1) return !!draft.personal?.full_name && !!draft.personal?.email && !!draft.personal?.phone && !!draft.personal?.location;
    if (s === 2) return !!draft.target?.primary_role;
    if (s === 3) {
      if ((draft.target?.experience_level || '').toLowerCase() === 'fresher') return true;
      return draft.experiences?.some(e => e.job_title && e.employer && e.start_date);
    }
    return true;
  };

  const missingWarnings = () => {
    const w = [];
    if (!draft.personal?.linkedin) w.push('LinkedIn URL not provided');
    if (!(draft.personal?.visa_status || draft.additional?.visa_status)) w.push('Visa status not provided');
    if (!(draft.personal?.availability || draft.additional?.availability)) w.push('Availability not provided');
    (draft.experiences || []).forEach(e => {
      if (!e.is_current && !e.end_date && e.employer) w.push(`End date missing for ${e.employer}`);
    });
    return w;
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      // Clean up empty rows before sending
      const cleaned = {
        ...draft,
        experiences: (draft.experiences || []).filter(e => e.job_title && e.employer).map(e => ({
          ...e,
          responsibilities: (e.responsibilities || []).filter(x => x?.trim()),
          achievements: (e.achievements || []).filter(x => x?.trim()),
          tools: (e.tools || []).filter(x => x?.trim()),
        })),
        education: (draft.education || []).filter(ed => ed.degree || ed.institution),
      };
      const r = await axios.post(`${API}/cv-builder/generate`, { draft: cleaned, confirmed: true });
      setGenerated(r.data.fixed_cv);
      toast.success('Your CV has been generated');
      fetchVersions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate CV');
    } finally { setGenerating(false); }
  };

  const download = async (id, kind) => {
    const setter = kind === 'pdf' ? setDownloadingPDF : setDownloadingDocx;
    setter(true);
    try {
      const url = kind === 'pdf'
        ? `${API}/cv/download-pdf/${id}?template=${template}`
        : `${API}/cv/download-docx/${id}?template=${template}`;
      const r = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([r.data]);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${(generated?.full_name || 'CV').replace(/\s+/g, '_')}_CV.${kind}`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      if (err.response?.status === 403) toast.error('Download requires a paid plan (Pro or CV Fix).');
      else toast.error(`Failed to download ${kind.toUpperCase()}`);
    } finally { setter(false); }
  };

  const openVersion = async (id) => {
    try {
      const r = await axios.get(`${API}/cv-builder/version/${id}`);
      setGenerated(r.data);
      setShowVersions(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error('Could not open this CV version.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#CE1126]" />
      </div>
    );
  }

  // -----------------------------------------------------------------
  //  GENERATED PREVIEW
  // -----------------------------------------------------------------
  if (generated) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />
        <main className="flex-1 container mx-auto px-4 py-8 max-w-4xl">
          <Card className="p-6 mb-6" data-testid="builder-generated">
            <div className="flex flex-wrap gap-3 justify-between items-center mb-4">
              <div>
                <h1 className="text-2xl font-bold">Your Optimized CV</h1>
                <p className="text-sm text-gray-500">Model: {generated.model}</p>
                <div className="mt-2 flex gap-2 items-center" data-testid="template-selector">
                  <span className="text-xs text-gray-600">Style:</span>
                  {[
                    { id: 'ats_professional', label: 'ATS Professional', tag: 'Recommended' },
                    { id: 'gulf_professional', label: 'Gulf Professional' },
                    { id: 'modern_professional', label: 'Modern' },
                  ].map(t => (
                    <button key={t.id} onClick={() => setTemplate(t.id)}
                      data-testid={`template-${t.id}`}
                      className={`text-xs px-2 py-1 rounded border ${template === t.id ? 'bg-[#CE1126] text-white border-[#CE1126]' : 'bg-white text-gray-700 border-gray-300'}`}>
                      {t.label}{t.tag && <span className="ml-1 opacity-70">({t.tag})</span>}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => download(generated.id, 'pdf')} disabled={downloadingPDF} className="btn-uae-red" data-testid="builder-download-pdf">
                  {downloadingPDF ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Preparing...</> : <><Download className="w-4 h-4 mr-2" />PDF</>}
                </Button>
                <Button variant="outline" onClick={() => download(generated.id, 'docx')} disabled={downloadingDocx} data-testid="builder-download-docx">
                  {downloadingDocx ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Preparing...</> : <><Download className="w-4 h-4 mr-2" />DOCX</>}
                </Button>
                <Button variant="outline" onClick={() => { setGenerated(null); setStep(1); setDraft(emptyDraft()); }}>Start New CV</Button>
              </div>
            </div>

            <CvPreview cv={generated} />

            {generated.jd_analysis?.missing?.length > 0 && (
              <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded" data-testid="builder-jd-gap">
                <p className="text-sm font-semibold text-amber-900 mb-2">Job Description gaps (not added to your CV)</p>
                <div className="flex flex-wrap gap-2">
                  {generated.jd_analysis.missing.map(k => (
                    <span key={k} className="bg-white text-amber-900 border border-amber-300 px-2 py-1 rounded text-xs">{k}</span>
                  ))}
                </div>
              </div>
            )}
          </Card>

          {versions.length > 1 && (
            <Card className="p-4" data-testid="builder-versions">
              <h3 className="font-semibold mb-2">My CV Versions</h3>
              <ul className="space-y-1 text-sm">
                {versions.slice(0, 5).map(v => (
                  <li key={v.id}>
                    <button onClick={() => openVersion(v.id)} className="w-full flex justify-between text-left text-gray-700 hover:text-[#CE1126] py-1" data-testid={`open-version-${v.id}`}>
                      <span>{v.headline || v.target_roles?.join(' | ') || 'CV'} <span className="text-xs text-gray-400">({v.source})</span></span>
                      <span className="text-xs text-gray-400">{new Date(v.created_at).toLocaleDateString()} · Open</span>
                    </button>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </main>
        <Footer />
      </div>
    );
  }

  // -----------------------------------------------------------------
  //  WIZARD
  // -----------------------------------------------------------------
  const warnings = missingWarnings();

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      <main className="flex-1 container mx-auto px-4 py-8 max-w-4xl">
        {/* Progress + save status */}
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-2xl font-bold">Build Your CV</h1>
          <div className="text-xs text-gray-500 flex items-center gap-2" data-testid="save-status">
            {saving ? <><Loader2 className="w-3 h-3 animate-spin" /> Saving...</> :
              savedAt
                ? <><Save className="w-3 h-3 text-[#009639]" /> Saved · {savedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</>
                : <><Save className="w-3 h-3" /> Draft saved automatically</>
            }
          </div>
        </div>

        {/* Step indicator */}
        <div className="flex gap-1 mb-6" data-testid="wizard-progress">
          {STEPS.map(s => (
            <div key={s.id} className={`flex-1 h-1.5 rounded ${s.id <= step ? 'bg-[#CE1126]' : 'bg-gray-200'}`} />
          ))}
        </div>
        <p className="text-sm text-gray-500 mb-6">Step {step} of {STEPS.length} · {STEPS[step - 1].label}</p>

        {/* Version history button */}
        {versions.length > 0 && !generated && (
          <div className="mb-4">
            <Button size="sm" variant="outline" onClick={() => setShowVersions(v => !v)} data-testid="toggle-versions-btn">
              <FileText className="w-3.5 h-3.5 mr-1" /> My CV Versions ({versions.length})
            </Button>
            {showVersions && (
              <Card className="mt-2 p-3" data-testid="version-history-list">
                <ul className="space-y-1 text-sm">
                  {versions.slice(0, 10).map(v => (
                    <li key={v.id}>
                      <button onClick={() => openVersion(v.id)} className="w-full flex justify-between text-left hover:text-[#CE1126] py-1" data-testid={`open-version-list-${v.id}`}>
                        <span>{v.headline || v.target_roles?.join(' | ') || 'CV'}</span>
                        <span className="text-xs text-gray-400">{v.source ? `${v.source} · ` : ''}{new Date(v.created_at).toLocaleDateString()} · Open</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </div>
        )}

        <Card className="p-6" data-testid={`wizard-step-${step}`}>
          {step === 1 && <Step1Personal draft={draft} update={update} />}
          {step === 2 && <Step2Target draft={draft} update={update} />}
          {step === 3 && <Step3Experience draft={draft} setDraft={setDraft} scheduleSave={scheduleSave} step={step} />}
          {step === 4 && <Step4Education draft={draft} setDraft={setDraft} scheduleSave={scheduleSave} step={step} />}
          {step === 5 && <Step5Skills draft={draft} update={update} />}
          {step === 6 && <Step6Additional draft={draft} update={update} />}
          {step === 7 && <Step7Review draft={draft} warnings={warnings} onGenerate={handleGenerate} generating={generating} onEdit={goStep} />}
        </Card>

        {/* Nav */}
        {step < 7 && (
          <div className="flex justify-between items-center mt-4 gap-2 flex-wrap">
            <Button variant="outline" onClick={() => goStep(step - 1)} disabled={step === 1} data-testid="wizard-prev">
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={saveAndExit} disabled={saving} data-testid="wizard-save-exit">
                <Save className="w-4 h-4 mr-1" /> Save &amp; Exit
              </Button>
              <Button className="btn-uae-red" onClick={() => {
                if (!stepValid(step)) { toast.error('Please fill required fields'); return; }
                goStep(step + 1);
              }} data-testid="wizard-next">
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
        {step === 7 && (
          <div className="flex justify-between items-center mt-4">
            <Button variant="outline" onClick={() => goStep(step - 1)} data-testid="wizard-prev">
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <Button variant="outline" onClick={saveAndExit} disabled={saving} data-testid="wizard-save-exit">
              <Save className="w-4 h-4 mr-1" /> Save &amp; Exit
            </Button>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}


// =============================================================================
//  Step subcomponents (kept in same file for cohesion)
// =============================================================================

const Field = ({ label, value, onChange, type = 'text', required, placeholder, testid }) => (
  <div>
    <label className="block text-xs font-medium text-gray-700 mb-1">{label}{required && ' *'}</label>
    <input type={type} value={value || ''} placeholder={placeholder}
      onChange={e => onChange(e.target.value)}
      className="w-full h-9 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#CE1126]"
      data-testid={testid} />
  </div>
);

const TextArea = ({ label, value, onChange, rows = 3, placeholder, testid }) => (
  <div>
    <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
    <textarea value={value || ''} rows={rows} placeholder={placeholder}
      onChange={e => onChange(e.target.value)}
      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#CE1126]"
      data-testid={testid} />
  </div>
);

function Step1Personal({ draft, update }) {
  const p = draft.personal || {};
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold">Personal Information</h2>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Full Name" required value={p.full_name} onChange={v => update('personal.full_name', v)} testid="wiz-full-name" />
        <Field label="Professional Email" type="email" required value={p.email} onChange={v => update('personal.email', v)} testid="wiz-email" />
        <Field label="Phone" required value={p.phone} onChange={v => update('personal.phone', v)} placeholder="+971..." testid="wiz-phone" />
        <Field label="Current Location" required value={p.location} onChange={v => update('personal.location', v)} placeholder="Dubai, UAE" testid="wiz-location" />
        <Field label="LinkedIn URL (optional)" value={p.linkedin} onChange={v => update('personal.linkedin', v)} testid="wiz-linkedin" />
        <Field label="Nationality (optional)" value={p.nationality} onChange={v => update('personal.nationality', v)} />
        <Field label="Visa Status (optional)" value={p.visa_status} onChange={v => update('personal.visa_status', v)} placeholder="e.g. Employment Visa (Transferable)" />
        <Field label="Driving Licence (optional)" value={p.driving_licence} onChange={v => update('personal.driving_licence', v)} placeholder="e.g. UAE Manual" />
        <Field label="Availability (optional)" value={p.availability} onChange={v => update('personal.availability', v)} placeholder="e.g. Immediate / 30 days notice" />
      </div>
      <div className="p-3 bg-gray-50 border rounded text-xs text-gray-600">
        Photo is optional and NOT ATS-recommended. You can add one later if desired.
      </div>
    </div>
  );
}

function Step2Target({ draft, update }) {
  const t = draft.target || {};
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold">Target Career</h2>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Primary Target Role" required value={t.primary_role} onChange={v => update('target.primary_role', v)} placeholder="e.g. Warehouse Supervisor" testid="wiz-primary-role" />
        <Field label="Target Country" value={t.country || 'UAE'} onChange={v => update('target.country', v)} testid="wiz-country" />
        <Field label="Secondary Role (optional)" value={t.secondary_role} onChange={v => update('target.secondary_role', v)} testid="wiz-secondary-role" />
        <Field label="Third Role (optional)" value={t.tertiary_role} onChange={v => update('target.tertiary_role', v)} testid="wiz-tertiary-role" />
        <Field label="Target Industry" value={t.industry} onChange={v => update('target.industry', v)} placeholder="e.g. Logistics" />
        <Field label="Preferred City (optional)" value={t.preferred_city} onChange={v => update('target.preferred_city', v)} />
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Career Stage</label>
          <select value={t.experience_level || 'Experienced'} onChange={e => update('target.experience_level', e.target.value)}
            className="w-full h-9 rounded-md border border-gray-300 px-2 text-sm" data-testid="wiz-experience-level">
            <option>Fresher</option>
            <option>Experienced</option>
          </select>
        </div>
      </div>
      <TextArea label="Paste Target Job Description (optional)" rows={5} value={draft.target_jd}
        onChange={v => update('target_jd', v)} testid="wiz-jd"
        placeholder="Paste the JD here — we'll flag any missing skills but never invent any." />
    </div>
  );
}

function Step3Experience({ draft, setDraft, scheduleSave, step }) {
  const exps = draft.experiences || [];
  const isFresher = (draft.target?.experience_level || '').toLowerCase() === 'fresher';

  const setExps = (newExps) => setDraft(prev => {
    const next = { ...prev, experiences: newExps };
    scheduleSave(next, step);
    return next;
  });

  const updateExp = (i, key, value) => {
    const nextExps = [...exps];
    nextExps[i] = { ...nextExps[i], [key]: value };
    setExps(nextExps);
  };

  const updateListField = (i, key, idx, value) => {
    const nextExps = [...exps];
    const list = [...(nextExps[i][key] || [])];
    list[idx] = value;
    nextExps[i] = { ...nextExps[i], [key]: list };
    setExps(nextExps);
  };

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold">Work Experience</h2>
      {isFresher && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-900">
          You marked yourself as a Fresher. Experience is optional. Skip to the next step if you have no professional experience yet.
        </div>
      )}

      {exps.map((exp, i) => (
        <Card key={i} className="p-4 border-dashed" data-testid={`exp-block-${i}`}>
          <div className="flex justify-between items-center mb-3">
            <p className="font-semibold text-sm text-gray-700">Experience #{i + 1}</p>
            {exps.length > 1 && (
              <Button size="sm" variant="outline" onClick={() => setExps(exps.filter((_, x) => x !== i))} data-testid={`exp-delete-${i}`}>
                <Trash2 className="w-3 h-3 mr-1" /> Remove
              </Button>
            )}
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Official Job Title" required value={exp.job_title} onChange={v => updateExp(i, 'job_title', v)} testid={`exp-title-${i}`} />
            <Field label="Company / Employer" required value={exp.employer} onChange={v => updateExp(i, 'employer', v)} testid={`exp-employer-${i}`} />
            <Field label="Location" value={exp.location} onChange={v => updateExp(i, 'location', v)} placeholder="Dubai, UAE" />
            <div className="flex items-end gap-2">
              <div className="flex-1"><Field label="Start (MMM YYYY)" required value={exp.start_date} onChange={v => updateExp(i, 'start_date', v)} placeholder="Mar 2021" testid={`exp-start-${i}`} /></div>
              <div className="flex-1">
                {exp.is_current
                  ? <div><label className="block text-xs font-medium text-gray-700 mb-1">End</label><div className="h-9 flex items-center text-sm text-gray-500">Present</div></div>
                  : <Field label="End (MMM YYYY)" value={exp.end_date} onChange={v => updateExp(i, 'end_date', v)} placeholder="Feb 2024" testid={`exp-end-${i}`} />}
              </div>
            </div>
          </div>
          <label className="flex items-center gap-2 mt-3 text-sm">
            <input type="checkbox" checked={!!exp.is_current} onChange={e => updateExp(i, 'is_current', e.target.checked)} data-testid={`exp-current-${i}`} />
            I currently work here
          </label>

          <div className="mt-3">
            <p className="text-xs font-medium text-gray-700 mb-1">Main Responsibilities</p>
            {(exp.responsibilities || ['']).map((r, ridx) => (
              <input key={ridx} type="text" value={r || ''} placeholder="What you do in this role..."
                onChange={e => updateListField(i, 'responsibilities', ridx, e.target.value)}
                className="w-full h-9 rounded-md border border-gray-300 px-3 text-sm mb-1"
                data-testid={`exp-resp-${i}-${ridx}`} />
            ))}
            <Button size="sm" variant="outline" onClick={() => updateExp(i, 'responsibilities', [...(exp.responsibilities || []), ''])}>
              <Plus className="w-3 h-3 mr-1" />Add
            </Button>
          </div>

          <div className="mt-3">
            <p className="text-xs font-medium text-gray-700 mb-1">Genuine Achievements (with real numbers only — optional)</p>
            {(exp.achievements || ['']).map((a, aidx) => (
              <input key={aidx} type="text" value={a || ''} placeholder="e.g. Reduced discrepancies by 35%"
                onChange={e => updateListField(i, 'achievements', aidx, e.target.value)}
                className="w-full h-9 rounded-md border border-gray-300 px-3 text-sm mb-1" />
            ))}
            <Button size="sm" variant="outline" onClick={() => updateExp(i, 'achievements', [...(exp.achievements || []), ''])}>
              <Plus className="w-3 h-3 mr-1" />Add
            </Button>
          </div>

          <div className="mt-3">
            <p className="text-xs font-medium text-gray-700 mb-1">Tools / Software Actually Used (optional)</p>
            <input type="text" value={(exp.tools || []).join(', ')} placeholder="SAP, Excel, WMS"
              onChange={e => updateExp(i, 'tools', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
              className="w-full h-9 rounded-md border border-gray-300 px-3 text-sm" />
          </div>
        </Card>
      ))}
      <Button variant="outline" onClick={() => setExps([...exps, { ...EMPTY_EXP }])} data-testid="exp-add">
        <Plus className="w-4 h-4 mr-1" /> Add Another Experience
      </Button>
    </div>
  );
}

function Step4Education({ draft, setDraft, scheduleSave, step }) {
  const edu = draft.education || [];
  const setEdu = (arr) => setDraft(prev => { const n = { ...prev, education: arr }; scheduleSave(n, step); return n; });
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold">Education</h2>
      {edu.map((e, i) => (
        <Card key={i} className="p-4 border-dashed">
          <div className="flex justify-between items-center mb-3">
            <p className="font-semibold text-sm text-gray-700">Education #{i + 1}</p>
            {edu.length > 1 && <Button size="sm" variant="outline" onClick={() => setEdu(edu.filter((_, x) => x !== i))}><Trash2 className="w-3 h-3 mr-1" />Remove</Button>}
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Degree / Qualification" value={e.degree} onChange={v => setEdu(edu.map((x, xi) => xi === i ? { ...x, degree: v } : x))} testid={`edu-degree-${i}`} />
            <Field label="Field of Study" value={e.field_of_study} onChange={v => setEdu(edu.map((x, xi) => xi === i ? { ...x, field_of_study: v } : x))} />
            <Field label="Institution" value={e.institution} onChange={v => setEdu(edu.map((x, xi) => xi === i ? { ...x, institution: v } : x))} testid={`edu-institution-${i}`} />
            <Field label="Location / Country" value={e.location} onChange={v => setEdu(edu.map((x, xi) => xi === i ? { ...x, location: v } : x))} />
            <Field label="Graduation Year" value={e.year} onChange={v => setEdu(edu.map((x, xi) => xi === i ? { ...x, year: v } : x))} testid={`edu-year-${i}`} />
            <Field label="Grade / CGPA (optional)" value={e.grade} onChange={v => setEdu(edu.map((x, xi) => xi === i ? { ...x, grade: v } : x))} />
          </div>
        </Card>
      ))}
      <Button variant="outline" onClick={() => setEdu([...edu, { ...EMPTY_EDU }])}><Plus className="w-4 h-4 mr-1" />Add Another Education</Button>
    </div>
  );
}

const ListInput = ({ label, list, onChange, placeholder, testid }) => (
  <div>
    <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
    <input type="text" value={(list || []).join(', ')} placeholder={placeholder}
      onChange={e => onChange(e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
      className="w-full h-9 rounded-md border border-gray-300 px-3 text-sm"
      data-testid={testid} />
    <p className="text-xs text-gray-400 mt-1">Comma-separated</p>
  </div>
);

function Step5Skills({ draft, update }) {
  const s = draft.skills || {};
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold">Skills & Credentials</h2>
      <ListInput label="Core Skills" list={s.core_skills} onChange={v => update('skills.core_skills', v)} placeholder="Team Leadership, Inventory Control" testid="wiz-core-skills" />
      <ListInput label="Tools / Software" list={s.tools_software} onChange={v => update('skills.tools_software', v)} placeholder="SAP, Excel, Oracle" testid="wiz-tools" />

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Languages (name — proficiency, one per line)</label>
        <textarea rows={3} value={(s.languages || []).map(l => `${l.language}${l.proficiency ? ' — ' + l.proficiency : ''}`).join('\n')}
          onChange={e => {
            const parsed = e.target.value.split('\n').filter(x => x.trim()).map(line => {
              const [lang, prof] = line.split('—').map(x => x.trim());
              return { language: lang || '', proficiency: prof || '' };
            });
            update('skills.languages', parsed);
          }}
          placeholder="English — Fluent&#10;Arabic — Basic"
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" data-testid="wiz-languages" />
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Certifications (name — issuer — year, one per line)</label>
        <textarea rows={3} value={(s.certifications || []).map(c => `${c.name}${c.issuer ? ' — ' + c.issuer : ''}${c.year ? ' — ' + c.year : ''}`).join('\n')}
          onChange={e => {
            const parsed = e.target.value.split('\n').filter(x => x.trim()).map(line => {
              const [n, iss, y] = line.split('—').map(x => x.trim());
              return { name: n || '', issuer: iss || '', year: y || '' };
            });
            update('skills.certifications', parsed);
          }}
          placeholder="CILT Level 3 — CILT International — 2020"
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" data-testid="wiz-certs" />
      </div>

      <ListInput label="Trainings" list={s.trainings} onChange={v => update('skills.trainings', v)} placeholder="First Aid, Forklift Handling" />
    </div>
  );
}

function Step6Additional({ draft, update }) {
  const a = draft.additional || {};
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-bold">Additional Information (optional)</h2>
      <TextArea label="Projects" value={(a.projects || []).join('\n')} onChange={v => update('additional.projects', v.split('\n').filter(Boolean))} />
      <TextArea label="Awards" value={(a.awards || []).join('\n')} onChange={v => update('additional.awards', v.split('\n').filter(Boolean))} />
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Notice Period" value={a.notice_period} onChange={v => update('additional.notice_period', v)} />
        <Field label="Portfolio URL" value={a.portfolio} onChange={v => update('additional.portfolio', v)} />
        <Field label="Visa Status" value={a.visa_status} onChange={v => update('additional.visa_status', v)} placeholder="If not filled in Step 1" />
        <Field label="Availability" value={a.availability} onChange={v => update('additional.availability', v)} placeholder="If not filled in Step 1" />
      </div>
    </div>
  );
}

function Step7Review({ draft, warnings, onGenerate, generating, onEdit }) {
  const p = draft.personal || {};
  const t = draft.target || {};
  const targetRoles = [t.primary_role, t.secondary_role, t.tertiary_role].filter(Boolean);
  return (
    <div className="space-y-5" data-testid="review-step">
      <h2 className="text-lg font-bold">Review Your Information</h2>
      <p className="text-sm text-gray-600">Please confirm the facts below. Our expert Gulf/UAE engine will optimize the presentation — but will never change your facts.</p>

      {warnings.length > 0 && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded" data-testid="review-warnings">
          <p className="text-sm font-semibold text-amber-900 flex items-center gap-2 mb-2"><AlertTriangle className="w-4 h-4" /> Optional Information Missing</p>
          <ul className="text-sm text-amber-900 space-y-1 pl-6 list-disc">
            {warnings.map(w => <li key={w}>{w}</li>)}
          </ul>
          <p className="text-xs text-amber-800 mt-2">You can skip these and generate now, or go back and add them.</p>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        <ReviewBlock title="Contact" onEdit={() => onEdit(1)}>
          <p><b>{p.full_name || '—'}</b></p>
          <p className="text-gray-600">{[p.phone, p.email, p.location, p.linkedin].filter(Boolean).join(' · ')}</p>
        </ReviewBlock>
        <ReviewBlock title="Target Career" onEdit={() => onEdit(2)}>
          <p>{targetRoles.join(' | ') || '—'}</p>
          <p className="text-gray-500 text-xs">{t.industry} · {t.country || 'UAE'} · {t.experience_level}</p>
        </ReviewBlock>
      </div>

      <ReviewBlock title={`Work Experience (${(draft.experiences || []).filter(e => e.employer).length})`} onEdit={() => onEdit(3)}>
        {(draft.experiences || []).filter(e => e.employer).map(e => (
          <p key={`${e.employer}-${e.job_title}-${e.start_date}`}>
            <b>{e.job_title}</b> @ {e.employer} <span className="text-gray-500 text-xs">({e.start_date} — {e.is_current ? 'Present' : e.end_date || ''})</span>
          </p>
        ))}
      </ReviewBlock>

      <ReviewBlock title={`Education (${(draft.education || []).filter(e => e.degree || e.institution).length})`} onEdit={() => onEdit(4)}>
        {(draft.education || []).filter(e => e.degree || e.institution).map(e => (
          <p key={`${e.degree}-${e.institution}-${e.year}`}>{e.degree}{e.field_of_study ? ' in ' + e.field_of_study : ''} — {e.institution} <span className="text-xs text-gray-500">({e.year})</span></p>
        ))}
      </ReviewBlock>

      <ReviewBlock title="Skills" onEdit={() => onEdit(5)}>
        <p className="text-xs text-gray-500">Core</p>
        <p>{(draft.skills?.core_skills || []).join(', ') || '—'}</p>
        <p className="text-xs text-gray-500 mt-2">Certifications</p>
        <p>{(draft.skills?.certifications || []).map(c => c.name).filter(Boolean).join(', ') || '—'}</p>
      </ReviewBlock>

      <div className="flex justify-between items-center pt-4 border-t">
        <Button variant="outline" onClick={() => onEdit(1)}>Edit Information</Button>
        <Button className="btn-uae-green" onClick={onGenerate} disabled={generating} data-testid="confirm-generate-btn">
          {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating (Gulf expert)...</> : <><CheckCircle2 className="w-4 h-4 mr-2" />Confirm &amp; Generate CV</>}
        </Button>
      </div>
    </div>
  );
}

const ReviewBlock = ({ title, children, onEdit }) => (
  <div className="p-3 border rounded bg-gray-50">
    <div className="flex justify-between items-start mb-2">
      <p className="text-xs font-semibold uppercase text-gray-500 tracking-wide">{title}</p>
      <button onClick={onEdit} className="text-xs text-[#CE1126] hover:underline">Edit</button>
    </div>
    <div className="text-sm text-gray-800 space-y-1">{children}</div>
  </div>
);

const CvPreview = ({ cv }) => (
  <div className="prose prose-sm max-w-none bg-white p-6 border rounded" data-testid="cv-preview">
    <h1 className="text-2xl font-bold mb-1 text-center">{cv.full_name}</h1>
    {cv.headline && <p className="text-center text-gray-700 text-sm">{cv.headline}</p>}
    <p className="text-center text-gray-500 text-sm">{[cv.phone, cv.email, cv.location, cv.linkedin].filter(Boolean).join(' · ')}</p>
    {cv.professional_summary && <><h2 className="text-[#CE1126] mt-4">Professional Summary</h2><p>{cv.professional_summary}</p></>}
    {cv.core_competencies?.length > 0 && <><h2 className="text-[#CE1126]">Core Competencies</h2><p>{cv.core_competencies.join(' • ')}</p></>}
    {cv.experience?.length > 0 && (
      <>
        <h2 className="text-[#CE1126]">Professional Experience</h2>
        {cv.experience.map(e => (
          <div key={`${e.employer}-${e.job_title}-${e.start_date}`} className="mb-3">
            <p><b>{e.job_title}</b> — {e.employer} <span className="text-xs text-gray-500">({e.start_date} — {e.end_date || (e.is_current ? 'Present' : '')})</span></p>
            <ul className="mt-1">
              {(e.bullets || e.responsibilities || []).map((b, i) => <li key={`${e.employer}-b-${i}`}>{b}</li>)}
            </ul>
          </div>
        ))}
      </>
    )}
    {cv.education?.length > 0 && (
      <>
        <h2 className="text-[#CE1126]">Education</h2>
        {cv.education.map(e => (
          <p key={`${e.degree}-${e.institution}-${e.year}`}>{e.degree}{e.field_of_study ? ' in ' + e.field_of_study : ''} — {e.institution} ({e.year})</p>
        ))}
      </>
    )}
  </div>
);
